"use client";

import React from "react";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import TradingJournalScene, { TradingJournalSceneMobile } from "./tradingJournal/TradingJournalScene";

import orangeBG from "@/public/home/orange-bg-trading-journal.jpg";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_ITEM = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.1 };
const ASSET_TRANSITION_BASE = { duration: 0.8, ease: [...TRANSITION_EASING] };

function TradingJournalSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { tradingJournalChip, tradingJournalTitle, tradingJournalAssets } = data;

  return (
    <section className="hidden lg:block relative w-full bg-[#040404] overflow-x-clip pt-[20rem]">
      <div className="w-[min(180rem,100%)] flex flex-col gap-[4rem] mx-auto">
        <div className="flex flex-col gap-[3rem]">
          {tradingJournalChip && (
            <motion.div
              className="w-fit"
              {...REVEAL_CHIP}
              transition={CHIP_TRANSITION}>
              <Chip
                text={tradingJournalChip}
                animatedIcon
                spinnerSize={32}
                spinnerVariant="snake"
                className="[&_*]:text-white"
              />
            </motion.div>
          )}

          {tradingJournalTitle && (
            <motion.div
              className="richtext richtext-start w-[min(74rem,100%)] prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-transparent prose-h1:mb-[3rem] prose-p:text-white/80"
              {...REVEAL_TITLE}
              transition={TITLE_TRANSITION}>
              <PortableText
                value={tradingJournalTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        <div className="flex relative flex-row w-full gap-[2rem] overflow-clip h-[76.625rem]">
          <Image
            src={orangeBG}
            alt=""
            quality={100}
            className="object-cover absolute w-full !h-full z-[1]"
          />

          <TradingJournalScene className="z-[2]" />
          <div className="pointer-events-none absolute z-[5] right-0 h-full w-[20%] bg-gradient-to-r from-transparent to-[#040404]" />
        </div>

        {tradingJournalAssets && tradingJournalAssets.length > 0 && (
          <div className="grid w-full grid-cols-4 gap-[6rem]">
            {tradingJournalAssets.map((asset, index) => (
              <motion.div
                key={index}
                className="flex flex-col gap-[1.5rem]"
                {...REVEAL_ITEM}
                transition={{ ...ASSET_TRANSITION_BASE, delay: index * 0.1 }}>
                <div className="flex items-center gap-[1rem]">
                  {asset.icon && (
                    <Image
                      className="!w-[3rem]"
                      src={asset.icon}
                      alt=""
                    />
                  )}
                  <h3 className="text-white m-0 font-spacemono text-[2rem] uppercase leading-[110%]">{asset.title}</h3>
                </div>

                {asset.text && (
                  <div className="richtext richtext-start prose-p:text-white/80 prose-p:text-[2.2rem] prose-p:leading-[150%]">
                    <PortableText
                      value={asset.text}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TradingJournalSectionMobile({ data }: { data: PageHomeSchema }) {
  const { tradingJournalChip, tradingJournalTitle, tradingJournalAssets } = data;

  return (
    <section className="lg:hidden relative w-full bg-[#040404] overflow-x-clip pt-[6rem]">
      <div className="w-[min(180rem,100%)] flex flex-col gap-[4rem] mx-auto px-[3rem]">
        <div className="flex flex-col gap-[3rem]">
          {tradingJournalChip && (
            <div className="w-fit">
              <Chip
                text={tradingJournalChip}
                spinnerSize={32}
                spinnerVariant="snake"
                className="[&_*]:text-white"
              />
            </div>
          )}

          {tradingJournalTitle && (
            <div className="richtext richtext-start w-[min(74rem,100%)] prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-transparent prose-h1:mb-[3rem] prose-p:text-white/80">
              <PortableText
                value={tradingJournalTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div className="flex relative flex-col w-full gap-[2rem] overflow-clip h-[36rem]">
          <Image
            src={orangeBG}
            alt=""
            quality={100}
            className="object-cover absolute w-full !h-full z-[1]"
          />

          <TradingJournalSceneMobile className="z-[2]" />
          <div className="pointer-events-none absolute z-[3] right-0 h-full w-[20%] bg-gradient-to-r from-transparent to-[#040404]" />
        </div>

        {tradingJournalAssets && tradingJournalAssets.length > 0 && (
          <div className="grid grid-cols-1 w-full gap-[4rem]">
            {tradingJournalAssets.map((asset, index) => (
              <div
                key={index}
                className="flex flex-col gap-[1.5rem]">
                <div className="flex items-center gap-[1rem]">
                  {asset.icon && (
                    <Image
                      className="!w-[3rem]"
                      src={asset.icon}
                      alt=""
                    />
                  )}
                  <h3 className="text-white m-0 font-spacemono text-[2rem] uppercase leading-[110%]">{asset.title}</h3>
                </div>

                {asset.text && (
                  <div className="richtext richtext-start prose-p:text-white/80 prose-p:text-[2.2rem] prose-p:leading-[150%]">
                    <PortableText
                      value={asset.text}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function TradingJournalSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <TradingJournalSectionDesktop data={data} />
      <TradingJournalSectionMobile data={data} />
    </>
  );
}
